import { useOrgContext, useOrgEditActions } from '@/org/contexts/OrgContext'
import MemberSearchInput from '@/search/components/MemberSearchInput'
import {
  Box,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tooltip,
  useDisclosure,
  useMediaQuery,
} from '@chakra-ui/react'
import { CircleFragment } from '@gql'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddMemberIcon } from 'src/icons'
import useCircleParentLinks from '../hooks/useCircleParentLinks'
import useCirclePermissions from '../hooks/useCirclePermissions'
import CircleParentLinkPickModal from '../modals/CircleParentLinkPickModal'

interface Props {
  circle: CircleFragment
  // Needed to name the two circles in the ambiguity modal
  parentCircle: CircleFragment
}

// Assign a member to a role without opening it: the picker opens right on the
// row. Not a MemberSearchButton, which needs a text label and turns into an
// input as wide as that label — a dozen of those would swamp the list.
//
// A leaf role takes the member directly. A sub-circle takes them through one of
// its parent-link roles; when that target is ambiguous (none, or several), the
// modal asks.
export default function CircleMemberAddButton({ circle, parentCircle }: Props) {
  const { t } = useTranslation()
  const { orgData } = useOrgContext()
  const { addCircleMember } = useOrgEditActions()
  const perms = useCirclePermissions(circle.id)
  const { isOpen, onOpen, onClose } = useDisclosure()
  // A search field inside a popover is unusable on a phone (see EmojiPicker)
  const [isMobile] = useMediaQuery('(max-width: 768px)')

  const role = orgData?.getRole(circle.roleId)
  const roleName = role?.name ?? ''
  const isCircle = orgData?.isCircle(circle.id) ?? false

  const links = useCircleParentLinks(isCircle ? circle.id : undefined)
  const openLinks = useMemo(
    () => links.filter((link) => link.canTakeMember && link.canEditMembers),
    [links]
  )

  const membersIds = useMemo(
    () => orgData?.membersOf(circle.id).map((cm) => cm.member.id) ?? [],
    [orgData, circle.id]
  )

  // The ambiguity modal, opened once the member is chosen
  const [pickedMemberId, setPickedMemberId] = useState<string | undefined>()
  const pickModal = useDisclosure()

  // Focus the search field ourselves once open. SearchInput only unfolds its
  // list on focus, and neither Chakra's autoFocus nor the input's own lands on
  // it here — the field would need a second click. SearchInput forwards no ref,
  // hence reaching for it through the container.
  const contentRef = useRef<HTMLElement | null>(null)
  useEffect(() => {
    if (!isOpen) return
    const timer = window.setTimeout(
      () => contentRef.current?.querySelector('input')?.focus(),
      0
    )
    return () => window.clearTimeout(timer)
  }, [isOpen])

  const handleSelect = useCallback(
    (memberId: string) => {
      onClose()
      if (!isCircle) {
        addCircleMember(circle.id, memberId)
        return
      }
      // Exactly one target: no question worth asking.
      if (openLinks.length === 1) {
        addCircleMember(openLinks[0].circle.id, memberId)
        return
      }
      // None, or several: only the user knows which.
      setPickedMemberId(memberId)
      pickModal.onOpen()
    },
    [circle.id, isCircle, openLinks, addCircleMember, onClose, pickModal]
  )

  // Show the button only when the gesture it triggers is allowed and possible.
  const isFullLeafRole = !!role?.singleMember && membersIds.length >= 1
  const canAdd = isCircle
    ? // Assign into a link role that can take someone, or create the missing one
      openLinks.length > 0 ||
      (links.length === 0 && perms.canEditSubCirclesParentLinks)
    : perms.canEditMembers && !isFullLeafRole

  if (!canAdd) return null

  const search = (
    <MemberSearchInput
      excludeIds={isCircle ? undefined : membersIds}
      onChange={handleSelect}
      autoFocus
      placeholder={t('CircleMemberAddButton.placeholder')}
    />
  )

  return (
    <>
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        isLazy
        boundary="scrollParent"
        placement="bottom-start"
        // Let Chakra focus the search field on open: SearchInput opens its list
        // on focus, so without this the list needs a second click. (EmojiPicker
        // sets autoFocus={false} because its picker focuses itself.)
        returnFocusOnClose={false}
      >
        {/* Both PopoverTrigger and Tooltip clone their child to inject a ref,
            so neither can wrap the other: Tooltip outside drops the popover's
            ref (no tooltip), PopoverTrigger outside hands its onClick to a
            Tooltip that never forwards it (dead button). The Box takes the
            trigger props and lets the click bubble up from the button. */}
        <PopoverTrigger>
          <Box display="inline-flex">
            <Tooltip
              label={t('CircleMemberAddButton.tooltip', { role: roleName })}
              placement="top"
              hasArrow
            >
              <IconButton
                aria-label={t('CircleMemberAddButton.tooltip', {
                  role: roleName,
                })}
                icon={<AddMemberIcon size={18} />}
                variant="ghost"
                size="sm"
              />
            </Tooltip>
          </Box>
        </PopoverTrigger>

        <Portal>
          {isMobile ? (
            <Modal isOpen={isOpen} isCentered size="sm" onClose={onClose}>
              <ModalOverlay />
              <ModalContent ref={contentRef}>
                <ModalBody p={4}>{search}</ModalBody>
              </ModalContent>
            </Modal>
          ) : (
            <PopoverContent ref={contentRef} w="auto" minW="20rem">
              <PopoverBody p={2}>{search}</PopoverBody>
            </PopoverContent>
          )}
        </Portal>
      </Popover>

      {pickModal.isOpen && pickedMemberId && (
        <CircleParentLinkPickModal
          circle={circle}
          parentCircle={parentCircle}
          memberId={pickedMemberId}
          isOpen
          onClose={pickModal.onClose}
        />
      )}
    </>
  )
}
